// Traveller avatar: a DiceBear "Lorelei" character, rendered locally.
//
// WHY LORELEI, AND WHY SELF HOSTED
// The product's core scenario is several independent travellers landing on the
// SAME pin point, so the avatar has to be the thing that tells four strangers
// apart at 28px. Lorelei gives thousands of distinct hand drawn characters
// from one seed, and Brian picked this look directly.
//   - Licence CC0 1.0, remix of Lorelei by Lisa Wischofsky. Public domain, no
//     attribution obligation, commercial use fine.
//   - Rendered from the bundled style definition, never from
//     api.dicebear.com: the app is offline first and the API sets
//     `default-src 'self'`, so a CDN avatar would be both broken offline and
//     blocked by the CSP.
//   - Deterministic: one seed always produces a byte identical drawing, so a
//     traveller keeps the same face across devices and restarts.
//   - Flat black ink on white. No gradient, no filter, no glow, and no emoji:
//     these are vector shapes, not glyphs.
import { useId } from "react";
import { Avatar as DiceBearAvatar, Style } from "@dicebear/core";
import definition from "@dicebear/styles/lorelei.json" with { type: "json" };

/** Fixed size token set. Never pass an arbitrary pixel value. */
export const AVATAR_SIZES = {
  xs: 28,
  sm: 36,
  md: 44,
  lg: 64,
} as const;

export type AvatarSize = keyof typeof AVATAR_SIZES;

const style = new Style(definition as never);

/**
 * Rendering a character is not free and the live map mounts many of them, so
 * each seed is drawn once and reused for the lifetime of the tab.
 */
const svgCache = new Map<string, string | null>();

/** Tokens that must never reach the DOM from a generated drawing. */
const BANNED = ["<script", "gradient", "<filter", "feGaussianBlur", "onload="];

/**
 * The generated markup, stripped down to the shapes: DiceBear's RDF metadata
 * block and comments carry roughly a kilobyte per avatar and nothing visual.
 * Returns null when the output fails the palette and safety audit, which
 * makes the initials fallback the safe default rather than injecting markup
 * this product has not vetted.
 */
export function loreleiShapes(seed: string): string | null {
  const cached = svgCache.get(seed);
  if (cached !== undefined) return cached;

  let shapes: string | null = null;
  try {
    const svg = new DiceBearAvatar(style, { seed }).toString();
    const inner = svg
      .replace(/<\?xml[\s\S]*?\?>/g, "")
      .replace(/<!--[\s\S]*?-->/g, "")
      .replace(/<metadata[\s\S]*?<\/metadata>/g, "")
      .replace(/^[\s\S]*?<svg[^>]*>/, "")
      .replace(/<\/svg>\s*$/, "")
      .trim();
    const lowered = inner.toLowerCase();
    shapes = BANNED.some((token) => lowered.includes(token)) ? null : inner;
  } catch {
    shapes = null;
  }

  svgCache.set(seed, shapes);
  return shapes;
}

/** DiceBear draws Lorelei on a 980 unit square. */
export const AVATAR_VIEWBOX = "0 0 980 980";

export interface AvatarProps {
  /**
   * Stable identity, `PublicUser.avatarSeed` when the server sends one, the
   * user id otherwise. Same seed, same character, forever.
   */
  seed: string;
  /** Used for the accessible name and for the initials fallback. */
  displayName?: string | null;
  /** Last resort only, when neither a seed nor a photo can be used. */
  initials?: string | null;
  /** A real profile photo replaces the drawing, clipped to the same circle. */
  photoUrl?: string | null;
  size?: AvatarSize;
  /**
   * The signed in traveller's own avatar. Marked by a thicker neutral
   * outline, never by a different colour, so it still reads for anyone who
   * cannot separate two hues.
   */
  isSelf?: boolean;
  /** Accessible name. Omit inside a row that already names the person. */
  label?: string | null;
  className?: string;
}

export function Avatar({
  seed,
  displayName,
  initials,
  photoUrl,
  size = "sm",
  isSelf,
  label,
  className,
}: AvatarProps) {
  const px = AVATAR_SIZES[size];
  const rawId = useId();
  const clipId = `avatar-clip-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const classes = ["avatar", `avatar-${size}`, isSelf ? "avatar-self" : null, className]
    .filter(Boolean)
    .join(" ");
  const aria = label
    ? { role: "img" as const, "aria-label": label }
    : { "aria-hidden": true as const };

  if (photoUrl) {
    return (
      <span className={classes} style={{ width: px, height: px }} {...aria}>
        <img className="avatar-photo" src={photoUrl} alt="" width={px} height={px} />
      </span>
    );
  }

  const shapes = seed.trim() ? loreleiShapes(seed.trim()) : null;

  if (!shapes) {
    // No seed, or a drawing that failed the audit. A monogram is the
    // fallback, never the primary look.
    const fallback = (initials || displayName || "?").trim();
    return (
      <span
        className={`${classes} avatar-monogram`}
        style={{ width: px, height: px }}
        {...aria}
      >
        <span className="avatar-initials">{fallback.slice(0, 2).toUpperCase()}</span>
      </span>
    );
  }

  return (
    <span className={classes} style={{ width: px, height: px }} {...aria}>
      <svg width={px} height={px} viewBox={AVATAR_VIEWBOX} focusable={false} aria-hidden>
        <defs>
          <clipPath id={clipId}>
            <circle cx={490} cy={490} r={490} />
          </clipPath>
        </defs>
        {/* The circle is the surface: flat white, one hairline outline, no
            coloured disc behind the ink. */}
        <g clipPath={`url(#${clipId})`} dangerouslySetInnerHTML={{ __html: shapes }} />
      </svg>
    </span>
  );
}

/**
 * Base styles, same pattern as bentoStyles: consuming apps get a working
 * component without hand copying CSS. apps/web mirrors these in styles.css.
 */
export const avatarStyles = `
.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 50%;
  border: 1.5px solid var(--color-border-strong);
  background: var(--color-bg);
}
.avatar-self { border-width: 3px; border-color: var(--color-text); }
.avatar-photo { width: 100%; height: 100%; object-fit: cover; display: block; }
.avatar-monogram { font-weight: 650; background: var(--color-surface); }
.avatar-xs .avatar-initials { font-size: 11px; }
.avatar-sm .avatar-initials { font-size: 13px; }
.avatar-md .avatar-initials { font-size: 15px; }
.avatar-lg .avatar-initials { font-size: 22px; }
`;
